import { useEffect } from "react";
import { motion } from "framer-motion";
import { Skull, ArrowLeft, Mail, Globe, User, ShoppingCart } from "lucide-react";
import { useCart } from "@/lib/cart";
import CartDrawer from "@/components/CartDrawer";

const Section = ({ num, title, children }) => (
  <motion.section
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7 }}
    className="border border-blooddeep/50 bg-pulp p-8 sm:p-10"
  >
    <h2 className="font-impact uppercase tracking-widest text-xl sm:text-2xl text-paper mb-6">
      <span className="text-crimson mr-3">{num}</span>
      {title}
    </h2>
    <div className="space-y-4 font-type text-sm text-paper/70 leading-relaxed">{children}</div>
  </motion.section>
);

const Bullet = ({ children }) => (
  <li className="flex gap-3">
    <Skull size={13} className="text-crimson shrink-0 mt-1" />
    <span>{children}</span>
  </li>
);

const Sub = ({ title, children }) => (
  <div>
    <h3 className="font-impact uppercase tracking-wider text-base text-bone mb-2">{title}</h3>
    <div className="space-y-3">{children}</div>
  </div>
);

export default function Submissions() {
  const { count, setOpen } = useCart();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="submissions-page" className="relative min-h-screen bg-void newsprint-grain">
      <div className="absolute inset-0 halftone opacity-[0.06] pointer-events-none" />
      <div className="absolute inset-0 blood-vignette pointer-events-none" />

      <header className="relative border-b border-blooddeep/40 bg-void/85 backdrop-blur-xl">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 h-16 flex items-center justify-between">
          <a href="/" data-testid="submissions-brand" className="flex items-center gap-2 group">
            <img src="/covers/atrocity-logo.webp" alt="Atrocity Comix logo" className="h-6 w-6 rounded-full transition-transform duration-200 group-hover:rotate-12" />
            <span className="font-display text-sm sm:text-base text-paper tracking-wide">
              ATROCITY <span className="text-crimson">COMIX</span>
            </span>
          </a>
          <div className="flex items-center gap-4">
            <a
              href="/"
              data-testid="submissions-back-link"
              className="flex items-center gap-2 font-impact uppercase tracking-wider text-xs text-paper/70 transition-colors duration-150 hover:text-crimson"
            >
              <ArrowLeft size={14} /> Back to the Crypt
            </a>
            <a
              data-testid="nav-account-link"
              href="/account"
              className="p-2 text-paper transition-colors duration-150 hover:text-crimson"
              aria-label="Your account"
            >
              <User size={22} />
            </a>
            <button
              data-testid="nav-cart-trigger"
              onClick={() => setOpen(true)}
              className="relative p-2 text-paper transition-colors duration-150 hover:text-bloodglow"
              aria-label="Open cart"
            >
              <ShoppingCart size={22} />
              {count > 0 && (
                <span className="absolute -top-1 -right-1 bg-crimson text-white font-impact text-[10px] w-5 h-5 flex items-center justify-center rounded-full border border-black">
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      <main className="relative mx-auto max-w-5xl px-4 sm:px-6 py-16 sm:py-24">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="font-marker text-paper text-base sm:text-lg mb-3 -rotate-1 inline-block"
        >
          the gate is open. twice a year.
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase text-paper leading-none mb-8"
        >
          SUBMISSIONS <span className="text-crimson">POLICY</span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="font-type text-sm sm:text-base text-paper/70 leading-relaxed max-w-3xl mb-16"
        >
          Thank you for your interest in submitting your work to Atrocity Comix! We are a publisher
          dedicated to bold, innovative, and boundary-pushing stories in the comic book medium. We
          welcome submissions from creators of all backgrounds who share our passion for unique,
          thought-provoking, and visually stunning comics. Please review our submissions policy
          carefully before sending your work.
        </motion.p>

        <div className="space-y-8">
          <Section num="I" title="What We're Looking For">
            <p>
              At Atrocity Comix, we specialize in creator-owned comics that explore daring
              themes, unconventional narratives, and striking art styles. We are open to a variety
              of genres, including but not limited to:
            </p>
            <ul className="space-y-2">
              <Bullet>Dark fantasy, horror, and speculative fiction</Bullet>
              <Bullet>Science fiction and dystopian stories</Bullet>
              <Bullet>Crime, noir, and psychological thrillers</Bullet>
              <Bullet>Experimental and avant-garde comics</Bullet>
              <Bullet>Stories that challenge social norms or explore complex human experiences</Bullet>
            </ul>
            <p>
              We prioritize original, creator-owned projects with strong, character-driven stories
              and visually distinctive artwork. We do not accept fan fiction, adaptations of
              existing IP (unless you hold the rights), or derivative works.
            </p>
          </Section>

          <Section num="II" title="Submission Guidelines">
            <Sub title="1. Who Can Submit">
              <ul className="space-y-2">
                <Bullet>
                  We accept submissions from writers, artists, writer/artist teams, and creative
                  teams (e.g., writers, pencillers, inkers, colorists, letterers).
                </Bullet>
                <Bullet>
                  Submissions are open to creators worldwide, but all materials must be submitted in
                  English or accompanied by an English translation.
                </Bullet>
                <Bullet>
                  We encourage submissions from both established and emerging creators. No prior
                  publishing experience is required.
                </Bullet>
              </ul>
            </Sub>
            <Sub title="2. What to Submit">
              <p>
                We only accept completed pitches for comic book projects. We do not review scripts,
                concept art, or partial submissions unless specifically requested. A complete
                submission package should include:
              </p>
              <ul className="space-y-2">
                <Bullet>
                  <strong className="text-bone">A Cover Letter (1 page or less):</strong> Introduce
                  yourself and your team (if applicable). Briefly describe your project, its genre,
                  target audience, and why it's a good fit for Atrocity Comix. Include any
                  relevant publishing credits or experience.
                </Bullet>
                <Bullet>
                  <strong className="text-bone">A Synopsis:</strong> A 1–2 page summary of your
                  story, including the premise, main characters, key plot points, and the story's
                  conclusion. Avoid vague teasers — be clear and specific.
                </Bullet>
                <Bullet>
                  <strong className="text-bone">A Pitch Document:</strong> A concise document (3–5
                  pages) that includes: the title of your project; the format (e.g., one-shot,
                  miniseries, ongoing series, graphic novel); the estimated page count or issue
                  count; a detailed outline of the story arc (for miniseries or ongoing series) or
                  the full story (for one-shots or graphic novels); a description of the visual
                  style and tone, including any specific artistic influences; and the target
                  audience (e.g., mature readers, young adult, all ages).
                </Bullet>
                <Bullet>
                  <strong className="text-bone">Sample Pages:</strong> 5–10 sequential pages of
                  fully inked and lettered artwork (or colored, if applicable). These pages should
                  showcase the storytelling, pacing, and art style of your project. If you're a
                  writer submitting without an artist, include a sample script (5–10 pages)
                  formatted in standard comic script format.
                </Bullet>
                <Bullet>
                  <strong className="text-bone">Character Designs or Concept Art (optional but
                  recommended):</strong> Include 1–3 character designs or concept sketches to give
                  us a sense of your visual world.
                </Bullet>
                <Bullet>
                  <strong className="text-bone">Creator Bios:</strong> A brief bio (100–200 words)
                  for each member of the creative team, highlighting relevant experience, skills, or
                  previous work.
                </Bullet>
              </ul>
            </Sub>
            <Sub title="3. Formatting Requirements">
              <ul className="space-y-2">
                <Bullet>All documents must be submitted as PDF files.</Bullet>
                <Bullet>
                  File names should follow this format: [CreatorLastName]_[ProjectTitle]_[DocumentType].pdf
                  (e.g., Smith_DarkHorizon_Synopsis.pdf).
                </Bullet>
                <Bullet>Sample pages should be high-resolution (300 DPI) and clearly legible.</Bullet>
                <Bullet>
                  Total submission package should not exceed 25 MB. If files are larger, provide a
                  link to a cloud storage service (e.g., Google Drive, Dropbox).
                </Bullet>
              </ul>
            </Sub>
            <Sub title="4. How to Submit">
              <ul className="space-y-2">
                <Bullet>
                  Submissions must be sent via email to{" "}
                  <span className="text-bloodglow font-courier">submissions@atrocitycomix.com</span>{" "}
                  with the subject line: "Comic Submission: [Project Title]."
                </Bullet>
                <Bullet>
                  We do not accept physical submissions, unsolicited materials sent via mail, or
                  submissions through social media or other platforms.
                </Bullet>
                <Bullet>Please do not send original artwork or irreplaceable materials.</Bullet>
              </ul>
            </Sub>
            <Sub title="5. Submission Periods">
              <ul className="space-y-2">
                <Bullet>
                  We accept submissions year-round, but we review submissions during specific
                  windows: <span className="text-bone">January 1–March 31</span> and{" "}
                  <span className="text-bone">July 1–September 30</span>. Submissions received
                  outside these periods will be held until the next review window.
                </Bullet>
                <Bullet>
                  Due to the high volume of submissions, we aim to respond within 8–12 weeks after
                  the close of each submission window. Please do not follow up on your submission
                  before this time.
                </Bullet>
              </ul>
            </Sub>
          </Section>

          <Section num="III" title="What to Expect">
            <ul className="space-y-2">
              <Bullet>
                <strong className="text-bone">Review Process:</strong> Our editorial team will
                review your submission for quality, originality, and alignment with our publishing
                vision. We may request additional materials or revisions if your project shows
                promise.
              </Bullet>
              <Bullet>
                <strong className="text-bone">Creator-Owned Terms:</strong> Atrocity Comix
                primarily publishes creator-owned works. Creators retain full ownership of their
                intellectual property, and terms of publication (including royalties, rights, and
                responsibilities) will be outlined in a contract if your project is selected.
              </Bullet>
              <Bullet>
                <strong className="text-bone">Feedback Policy:</strong> Due to the volume of
                submissions, we cannot provide detailed feedback on every submission. If your
                project is not selected, you will receive a brief notification. We encourage
                creators to continue refining their work and resubmitting in future windows.
              </Bullet>
            </ul>
          </Section>

          <Section num="IV" title="Important Notes">
            <ul className="space-y-2">
              <Bullet>
                <strong className="text-bone">Original Work Only:</strong> Submissions must be your
                own original work. Plagiarism, unauthorized use of intellectual property, or
                submissions that infringe on existing copyrights will be rejected and may result in
                legal action.
              </Bullet>
              <Bullet>
                <strong className="text-bone">No Simultaneous Submissions:</strong> We do not accept
                submissions that are under consideration by other publishers. If your project is
                submitted elsewhere, please inform us immediately.
              </Bullet>
              <Bullet>
                <strong className="text-bone">Diversity and Inclusion:</strong> Atrocity Comix
                is committed to amplifying diverse voices and stories. We encourage
                submissions from creators of all backgrounds, identities, and experiences.
              </Bullet>
              <Bullet>
                <strong className="text-bone">Mature Content:</strong> We are open to mature themes,
                but gratuitous violence, explicit content, or harmful stereotypes will be evaluated
                carefully. Content must serve the story and align with our mission to create
                thought-provoking comics.
              </Bullet>
            </ul>
          </Section>

          <Section num="V" title="Rights and Responsibilities">
            <p>By submitting to Atrocity Comix, you confirm that:</p>
            <ul className="space-y-2">
              <Bullet>
                You are the sole creator or have permission from all co-creators to submit the work.
              </Bullet>
              <Bullet>
                The submission does not violate any existing copyrights or intellectual property
                rights.
              </Bullet>
              <Bullet>
                You grant Atrocity Comix the right to review your submission for
                consideration. We do not claim ownership of your work unless a publishing agreement
                is signed.
              </Bullet>
            </ul>
          </Section>

          <Section num="VI" title="Contact">
            <ul className="space-y-2">
              <li className="flex gap-3 items-center">
                <Mail size={13} className="text-crimson shrink-0" />
                <span>
                  Questions about the process:{" "}
                  <a
                    data-testid="submissions-questions-email-link"
                    href="mailto:shop@atrocitycomix.com"
                    className="text-bloodglow font-courier underline decoration-blooddeep underline-offset-4 transition-colors duration-150 hover:text-crimson"
                  >
                    shop@atrocitycomix.com
                  </a>{" "}
                  — do not send submissions to this address.
                </span>
              </li>
              <li className="flex gap-3 items-center">
                <Mail size={13} className="text-crimson shrink-0" />
                <span>
                  Submissions:{" "}
                  <a
                    data-testid="submissions-email-link"
                    href="mailto:submissions@atrocitycomix.com?subject=Comic%20Submission%3A%20%5BProject%20Title%5D"
                    className="text-bloodglow font-courier underline decoration-blooddeep underline-offset-4 transition-colors duration-150 hover:text-crimson"
                  >
                    submissions@atrocitycomix.com
                  </a>
                </span>
              </li>
              <li className="flex gap-3 items-center">
                <Globe size={13} className="text-crimson shrink-0" />
                <a
                  data-testid="submissions-website-link"
                  href="https://atrocitycomix.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-courier text-bloodglow underline decoration-blooddeep underline-offset-4 transition-colors duration-150 hover:text-crimson"
                >
                  www.atrocitycomix.com
                </a>
              </li>
            </ul>
            <p>
              We look forward to discovering your unique vision and bringing bold new stories to the
              world of comics!
            </p>
            <p className="font-marker text-bloodglow text-lg rotate-[-1deg] inline-block mt-2">
              — The Atrocity Comix Editorial Team
            </p>
          </Section>
        </div>

        <p className="font-courier text-[11px] uppercase tracking-widest text-paper/40 text-center mt-16">
          © 2018 and beyond — Atrocity Comix. All rights reserved.
        </p>
      </main>
      <CartDrawer />
    </div>
  );
}
